import { Alert, capitalize, Typography } from '@mui/material';
import { AthletePreview } from '@strength/domain';
import {
  buildBowlingSummaryDays,
  CrossBlockBowlingRow,
  mergeWorkoutLayersByWorkoutId,
} from '@strength/domain-workout';
import { useAtom } from 'jotai';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '../../../../trpc/react';
import {
  clampRangeToBounds,
  clampRangeToMaxUniqueMonths,
  DateRange,
} from '../../../components/dates/RangeCalendar';
import { RangeCalendarSelect } from '../../../components/dates/RangeCalendarSelect';
import { AthleteSelect } from '../../../components/layout/athlete-select';
import { Sheet } from '../../../components/sheet';
import { useMetrics } from '../../../hooks/useMetrics';
import { useStorage } from '../../../hooks/useStorage';
import { useToast } from '../../../hooks/useToast';
import { getLocale } from '../../../utils/i18n';
import {
  buildWorkoutClipboard,
  workoutClipboardAtom,
} from '../copy-paste/workout-clipboard';
import { InstructionDrawer } from '../instruction-drawer';
import {
  AthleteDraftWorkoutLayer,
  BaseBlockSheetProps,
  InstructionEditData,
  WorkoutWithCycleAndSession,
} from '../types';
import { mergeWorkoutsWithCyclesAndSessions } from '../utils';
import { getWorkoutStatusesForAthlete } from '../workout-statuses';
import {
  getActualWorkoutsForLayers,
  getChartWorkoutsForLayers,
} from './actuals-workouts';
import {
  applyCrossBlockOverlayToActuals,
  applyCrossBlockOverlayToPrescriptions,
  buildCrossBlockChartWorkouts,
} from './cross-block-overlay';
import { BowlingLoadRatioTrendChart } from './charts/BowlingLoadRatioTrendChart';
import { BowlingSideBySideChart } from './charts/BowlingSideBySideChart';
import { BowlingStackedChart } from './charts/BowlingStackedChart';
import { clampPhasesToRange } from './clamp-phases-to-range';
import { ContextMenu } from './ContextMenu';
import { groupSelectionData } from './group-data';
import { getMonthViews } from './month-views';
import { getBlockSummarySlotsForMonth } from './slots/getBlockSummarySlots';
import { ActualWorkout } from './types';
import { useBlockSummaryMutations } from './useBlockSummaryMutations';
import { useCrossBlockBowlingOverlay } from './useCrossBlockBowlingOverlay';

const MAX_DATE_RANGE_MONTHS = 12;

type StoredPreferences = {
  athleteId: string | null;
  dateRange: DateRange | null;
};

const defaultStoredPreferences: StoredPreferences = {
  athleteId: null,
  dateRange: null,
};

function defaultDateRange(): DateRange {
  const now = DateTime.now();
  const start = now.minus({ months: 1 }).toISODate();
  const end = now.plus({ months: 1 }).toISODate();
  return clampRangeToMaxUniqueMonths(
    { start: start ?? '', end: end ?? '' },
    MAX_DATE_RANGE_MONTHS,
  );
}

// Build custom grid template cols: min 200px (type/intensity) + 36px * 31 days + 1fr (flexible); day columns fixed to avoid gaps
const gridTemplateColumns = `minmax(200px, 1fr) ${'36px '.repeat(31)}1fr`;

export type BlockSummaryProps = Omit<BaseBlockSheetProps, 'view'> & {
  athletes: AthletePreview[];
  // All athletes' draft layers for the block, shared with the page-level
  // subscription — the summary filters to the selected athlete in memory
  // rather than holding a second loco-sync query on the same model.
  athleteDraftWorkoutLayers: AthleteDraftWorkoutLayer[];
  selectedAthleteId: string | null;
  setSelectedAthleteId: (athleteId: string | null) => void;
};

export const BlockSummary = ({
  block,
  athletes,
  athleteDraftWorkoutLayers,
  selectedAthleteId,
  setSelectedAthleteId,
  blockWorkoutLayers,
  blockPublishedWorkoutLayers,
  modifiedPublishedWorkoutLayers,
  athletePublishedWorkoutLayers,
  actualWorkoutLayers,
  readOnly,
}: BlockSummaryProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { setsMetric, getMetric } = useMetrics();

  const [clipboard, setClipboard] = useAtom(workoutClipboardAtom);
  const { id: blockId } = block;
  const defaultRange = useMemo(() => defaultDateRange(), []);
  const [instructionEditData, setInstructionEditData] =
    useState<null | InstructionEditData>(null);

  const [storedPrefs, setStoredPrefs] = useStorage<StoredPreferences>(
    `block-summary-${block.id}`,
    defaultStoredPreferences,
  );

  // Defer showing the Alert until after first paint so we never flash it while selection is being restored.
  const [canShowAlert, setCanShowAlert] = useState(false);
  useEffect(() => {
    setCanShowAlert(true);
  }, []);

  const blockBounds = useMemo(() => {
    const start = DateTime.fromISO(block.startDate);
    const end = DateTime.fromISO(block.endDate);
    if (!start.isValid || !end.isValid) return null;
    return { start, end: start > end ? start : end };
  }, [block.startDate, block.endDate]);

  const clampToBlockBounds = useCallback(
    (range: DateRange): DateRange => {
      if (!blockBounds) return range;
      return clampRangeToBounds(range, blockBounds);
    },
    [blockBounds],
  );

  const [dateRange, selectedAthlete] = useMemo(() => {
    const rawRange =
      storedPrefs.dateRange?.start && storedPrefs.dateRange?.end
        ? clampRangeToMaxUniqueMonths(
            storedPrefs.dateRange,
            MAX_DATE_RANGE_MONTHS,
          )
        : defaultRange;
    const clamped = clampToBlockBounds(rawRange);
    return [
      clamped,
      athletes.find((a) => a.id === selectedAthleteId) ?? athletes[0] ?? null,
    ];
  }, [
    storedPrefs.dateRange,
    defaultRange,
    selectedAthleteId,
    athletes,
    clampToBlockBounds,
  ]);

  // When selectedAthleteId is null: restore from storage (once per block) or sync from resolved athlete (e.g. fallback to athletes[0]) and persist.
  const lastRestoredBlockId = useRef<string | null>(null);
  const hasAttemptedRestore = useRef(false);
  useEffect(() => {
    if (selectedAthleteId != null || athletes.length === 0) return;

    const hasValidStoredAthlete =
      storedPrefs.athleteId &&
      athletes.some((a) => a.id === storedPrefs.athleteId);
    const canRestoreFromStorage =
      lastRestoredBlockId.current !== block.id && hasValidStoredAthlete;

    if (canRestoreFromStorage && storedPrefs.athleteId) {
      lastRestoredBlockId.current = block.id;
      setSelectedAthleteId(storedPrefs.athleteId);
      return;
    }

    // Only sync to resolved athlete when there's no valid stored preference, so we don't overwrite a restore (e.g. before parent state has updated).
    if (selectedAthlete && !hasValidStoredAthlete) {
      setSelectedAthleteId(selectedAthlete.id);
      setStoredPrefs((prev) => ({ ...prev, athleteId: selectedAthlete.id }));
      return;
    }

    // We have athletes but couldn't restore or sync (e.g. stored id no longer valid and no fallback) — show "no selected athlete" only in this case so we don't flash on initial load.
    hasAttemptedRestore.current = true;
  }, [
    block.id,
    athletes,
    selectedAthleteId,
    selectedAthlete,
    storedPrefs.athleteId,
    setSelectedAthleteId,
    setStoredPrefs,
  ]);

  // Tombstoned layers (deletedAt set) must stay in the page-level query this
  // filters — the layout merge uses them to hide block-layer workouts that
  // were deleted for this athlete.
  const athleteDraftLayers = useMemo(
    () =>
      athleteDraftWorkoutLayers.filter(
        (layer) => layer.profileId === selectedAthlete?.id,
      ),
    [athleteDraftWorkoutLayers, selectedAthlete?.id],
  );

  const { data: crossBlockOverlapData } = useCrossBlockBowlingOverlay({
    block,
    dateRange,
  });
  const crossBlockOverlay = useMemo(
    () =>
      buildBowlingSummaryDays({
        currentBlockId: block.id,
        currentTeamId: block.teamId,
        crossBlockRows: (crossBlockOverlapData ?? []) as CrossBlockBowlingRow[],
      }),
    [block.id, block.teamId, crossBlockOverlapData],
  );

  const { data: rawPhases = [] } = trpc.phase.phases.useQuery(undefined, {
    staleTime: 10_000,
  });

  const clampedPhases = useMemo(
    () => clampPhasesToRange(rawPhases, dateRange),
    [rawPhases, dateRange],
  );

  const [
    modifiedAthleteLayers,
    actualAthleteLayers,
    athletePublishedLayers,
    prescriptionWorkouts,
    monthViews,
    chartWorkouts,
    hasActualWorkoutIds,
  ] = useMemo(() => {
    const modifiedAthleteLayers = modifiedPublishedWorkoutLayers.filter(
      (layer) => layer.profileId === selectedAthleteId,
    );
    const publishedAthleteLayers = athletePublishedWorkoutLayers.filter(
      (layer) => layer.profileId === selectedAthleteId,
    );
    const actualAthleteLayers = actualWorkoutLayers.filter(
      (layer) => layer.profileId === selectedAthleteId,
    );
    const workoutStatuses = getWorkoutStatusesForAthlete(
      blockWorkoutLayers,
      athleteDraftLayers,
      publishedAthleteLayers,
      actualAthleteLayers,
      blockPublishedWorkoutLayers,
    );

    const rawPrescriptionWorkouts = mergeWorkoutsWithCyclesAndSessions(
      block,
      blockWorkoutLayers,
      mergeWorkoutLayersByWorkoutId(blockWorkoutLayers, athleteDraftLayers),
      actualAthleteLayers,
      modifiedAthleteLayers,
      workoutStatuses,
      athleteDraftLayers,
    );

    // Dates already covered by the cross-block overlay render from the source
    // workout (disabled), so empty-day synthesis must skip them.
    const overlayDates = new Set(
      selectedAthleteId
        ? [...crossBlockOverlay.keys()]
            .filter((key) => key.startsWith(`${selectedAthleteId}|`))
            .map((key) => key.slice(selectedAthleteId.length + 1))
        : [],
    );

    const rawActualWorkouts = getActualWorkoutsForLayers(
      actualAthleteLayers,
      publishedAthleteLayers,
      blockPublishedWorkoutLayers,
      modifiedAthleteLayers,
      selectedAthleteId,
      {
        blockId,
        blockRange: { startDate: block.startDate, endDate: block.endDate },
        blockDraftWorkoutLayers: blockWorkoutLayers,
        athleteDraftWorkoutLayers: athleteDraftLayers,
        excludedDates: overlayDates,
      },
    );

    const prescriptionWorkouts = applyCrossBlockOverlayToPrescriptions(
      rawPrescriptionWorkouts,
      crossBlockOverlay,
      selectedAthleteId,
    );

    const actualWorkouts = applyCrossBlockOverlayToActuals(
      rawActualWorkouts,
      crossBlockOverlay,
      selectedAthleteId,
    );

    // The chart reflects the same draft state shown in the grid — block draft
    // and athlete draft layers — so edits show up immediately rather than only
    // after publish. Actuals are layered on top so completed sessions report
    // the recorded load instead of the prescription. Cross-block overlay
    // workouts (e.g. from a cross-team read-only block) are reconciled in so
    // the chart shows the same data visible in the grid cells — on an
    // overlapping day the overlay workout replaces the current-block one
    // rather than being counted alongside it.
    //
    // hasActualWorkoutIds tracks which of those workoutIds have a recorded
    // actual (block-draft actuals plus overlay entries' own hasActual flag) —
    // the load-trend chart needs it to apply the past-day exclusion rule
    // (see excludePastPlannedOnlyWorkouts), since the merged Workout itself
    // carries no actual-vs-planned provenance once the 'Prescription'
    // fallback sentinel is stripped.
    const { workouts: chartWorkouts, overlayActualWorkoutIds } =
      buildCrossBlockChartWorkouts(
        getChartWorkoutsForLayers(
          blockWorkoutLayers,
          athleteDraftLayers,
          modifiedAthleteLayers,
          actualAthleteLayers,
        ),
        crossBlockOverlay,
        selectedAthleteId,
      );
    const hasActualWorkoutIds = new Set([
      ...actualAthleteLayers.map((layer) => layer.workoutId),
      ...overlayActualWorkoutIds,
    ]);

    return [
      modifiedAthleteLayers,
      actualAthleteLayers,
      publishedAthleteLayers,
      prescriptionWorkouts,
      getMonthViews(dateRange, prescriptionWorkouts, actualWorkouts),
      chartWorkouts,
      hasActualWorkoutIds,
    ];
  }, [
    modifiedPublishedWorkoutLayers,
    actualWorkoutLayers,
    selectedAthleteId,
    blockId,
    blockWorkoutLayers,
    athleteDraftLayers,
    athletePublishedWorkoutLayers,
    blockPublishedWorkoutLayers,
    block,
    dateRange,
    crossBlockOverlay,
  ]);

  const allPrescriptionWorkouts = useMemo(
    () =>
      monthViews.flatMap(
        ({ prescriptionWorkoutsInRange }) => prescriptionWorkoutsInRange,
      ),
    [monthViews],
  );
  const allActualWorkouts = useMemo(
    () =>
      monthViews.flatMap(
        ({ actualsWorkoutsInRange }) => actualsWorkoutsInRange,
      ),
    [monthViews],
  );

  const {
    prescriptionMutationFns,
    actualsMutationFns,
    pastePrescription,
    undo,
    redo,
  } = useBlockSummaryMutations({
    blockId,
    profileId: selectedAthleteId ?? '',
    setsMetric,
    prescriptionWorkouts,
    athleteDraftLayers,
    blockPublishedLayers: blockPublishedWorkoutLayers,
    athletePublishedLayers,
    modifiedAthleteLayers,
    actualAthleteLayers,
  });

  const handleAthleteChange = (athleteId: string) => {
    setSelectedAthleteId(athleteId);
    setStoredPrefs((prev) => ({ ...prev, athleteId }));
  };

  const handleDateRangeChange = (range: DateRange | null) => {
    const toStore = range ? clampToBlockBounds(range) : null;
    setStoredPrefs((prev) => ({ ...prev, dateRange: toStore }));
  };

  // Show an alert if there are no athletes or if the selected athlete is not set and we have attempted to restore it.
  const noAthletes = athletes.length === 0;
  const noSelectedAthlete =
    selectedAthleteId === null && hasAttemptedRestore.current;
  const shouldShowAlert = canShowAlert && (noAthletes || noSelectedAthlete);

  if (!canShowAlert && noAthletes) return null;

  if (shouldShowAlert) {
    return (
      <Alert severity="error" className="m-6">
        {t('noAthletesBlockSummaryWarning')}
      </Alert>
    );
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col px-6 w-full">
      <div className="flex items-center gap-2 pt-4">
        <AthleteSelect
          athletes={athletes}
          selectedAthlete={selectedAthlete}
          onChange={(selectedAthleteId) => {
            if (!selectedAthleteId) return;
            handleAthleteChange(selectedAthleteId);
          }}
        />
        <RangeCalendarSelect
          value={dateRange}
          onChange={handleDateRangeChange}
          maxUniqueMonths={MAX_DATE_RANGE_MONTHS}
          minDate={block.startDate}
          maxDate={block.endDate}
        />
      </div>
      <div className="min-h-0 min-w-0 flex-1 overflow-y-auto w-full">
        {/** Charts above the table */}
        <div className="mt-6 flex flex-col gap-8">
          <BowlingLoadRatioTrendChart
            workouts={chartWorkouts}
            dateRange={dateRange}
            phases={clampedPhases}
            hasActualWorkoutIds={hasActualWorkoutIds}
          />
        </div>
        {/** For each month in the date range, render a div with the month name */}
        <div className="w-full">
          {monthViews.map(
            ({
              monthName,
              monthRange,
              prescriptionWorkoutsInRange,
              actualsWorkoutsInRange,
            }) => {
              const startDate = DateTime.fromISO(monthRange.start);
              const endDate = DateTime.fromISO(monthRange.end);

              if (!startDate.isValid || !endDate.isValid) return null;

              const month = startDate.month;
              const monthSlots = getBlockSummarySlotsForMonth(
                month,
                startDate.year,
                prescriptionWorkoutsInRange,
                actualsWorkoutsInRange,
                prescriptionMutationFns,
                actualsMutationFns,
                getMetric,
                t,
                readOnly,
                {
                  crossBlockOverlay,
                  profileId: selectedAthleteId,
                  locale: getLocale(),
                  prescriptionWorkouts: prescriptionWorkoutsInRange,
                },
              );

              return (
                <div key={monthRange.start} className="mt-4 w-full">
                  <Typography variant="h4" className="mb-2">
                    {capitalize(monthName)}
                  </Typography>
                  <Sheet
                    {...monthSlots}
                    className="min-w-full w-full overflow-auto overscroll-auto"
                    onUndo={undo}
                    onRedo={redo}
                    onBulkClear={() => {}}
                    gridTemplateColumns={gridTemplateColumns}
                    gridTemplateRows={`40px repeat(${Math.max(
                      0,
                      monthSlots.rowCount - 1,
                    )}, 30px)`}
                    readOnly={readOnly}
                    disableKeyboardAndMouse={readOnly || !!instructionEditData}
                    getContextMenuContent={(data) => (
                      <ContextMenu
                        data={data ?? []}
                        setInstructionEditData={setInstructionEditData}
                      />
                    )}
                    onCopy={(data) => {
                      const groupedData = groupSelectionData(data);
                      if (groupedData.length === 0) {
                        return;
                      }
                      const daysWithDates = groupedData.flatMap((wgd) => {
                        const workout = prescriptionWorkouts.find(
                          (w) => w.workoutId === wgd.workoutId,
                        );
                        if (!workout?.assignedDate) return [];
                        return [
                          { assignedDate: workout.assignedDate, data: wgd },
                        ];
                      });
                      if (daysWithDates.length === 0) return;
                      setClipboard(buildWorkoutClipboard(daysWithDates));
                    }}
                    onPaste={(_, targetData) => {
                      if (!clipboard) {
                        return;
                      }
                      if (targetData.length === 0) {
                        return;
                      }
                      const groupedTargetData = groupSelectionData(targetData);
                      const firstTarget = groupedTargetData[0];
                      if (!firstTarget) return;

                      const result = pastePrescription(
                        clipboard,
                        firstTarget.workoutId,
                        firstTarget,
                      );

                      if (!result.ok) {
                        toast({
                          title: result.error,
                          severity: 'error',
                        });
                      }
                    }}
                  />
                </div>
              );
            },
          )}
        </div>
        {/** Charts below the table */}
        <div className="mt-6 flex flex-col gap-8 pb-6">
          <BowlingSideBySideChart
            actualWorkouts={allActualWorkouts}
            prescriptionWorkouts={allPrescriptionWorkouts}
            phases={clampedPhases}
            dateRange={dateRange}
          />
          <BowlingStackedChart
            actualWorkouts={allActualWorkouts}
            prescriptionWorkouts={allPrescriptionWorkouts}
            phases={clampedPhases}
            dateRange={dateRange}
          />
        </div>
      </div>
      {instructionEditData && (
        <InstructionDrawer
          instruction={instructionEditData.instruction}
          setInstructionEditData={setInstructionEditData}
          editInstruction={(newInstruction) => {
            if (readOnly) return;
            // We expect an exercise id to be present
            // since we only allow adding/editing exercise instructions
            // in the block summary view
            if (instructionEditData.workoutExerciseId) {
              prescriptionMutationFns.onEditExercise(
                instructionEditData.workoutId,
                instructionEditData.workoutExerciseId,
                {
                  instruction: newInstruction,
                },
              );
            }
          }}
          maxLength={300}
        />
      )}
    </div>
  );
};
